import React, { useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { components } from 'cng-web-lib'

const {
  button: { CngButton }
} = components

const MIN_CHARS = 10
const MAX_CHARS = 512

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
    width: 520,
    maxWidth: '100%'
  },
  titleRoot: {
    padding: theme.spacing(2.5, 3, 1.5)
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  titleText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 700,
    fontSize: 16,
    color: theme.palette.error.main
  },
  closeBtn: {
    padding: 4,
    color: theme.palette.text.secondary
  },
  infoBox: {
    background: theme.palette.background.sectionOddBg || theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2.5)
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: theme.spacing(0.6, 0),
    '& + &': {
      borderTop: `1px solid ${theme.palette.divider}`
    }
  },
  infoLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: 500,
    flexShrink: 0,
    marginRight: theme.spacing(2)
  },
  infoValue: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    textAlign: 'right',
    wordBreak: 'break-all'
  },
  textArea: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8
    }
  },
  charHint: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginTop: 4
  },
  warningBox: {
    display: 'flex',
    gap: theme.spacing(1.5),
    background: theme.palette.warning.light + '33',
    border: `1px solid ${theme.palette.warning.light}`,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginTop: theme.spacing(2)
  },
  warningIcon: {
    color: theme.palette.warning.dark,
    marginTop: 2,
    flexShrink: 0
  },
  warningText: {
    fontSize: 12,
    color: theme.palette.text.primary,
    lineHeight: 1.6
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3)
  }
}))

/**
 * Reusable Cancel Bill dialog for MBL and HBL.
 *
 * Props:
 *  open         - boolean
 *  bill         - the row object from the table
 *  billType     - 'MASTER' | 'HOUSE'
 *  onClose      - fn()
 *  onSubmit     - fn(cancellationReason: string)
 *  submitting   - boolean (loading state)
 */
function CancelBillDialog({ open, bill, billType, onClose, onSubmit, submitting }) {
  const classes = useStyles()
  const [reason, setReason] = useState('')
  const [touched, setTouched] = useState(false)

  const isMaster = billType === 'MASTER'
  const title = isMaster ? 'Cancel Master Bill of Lading' : 'Cancel House Bill of Lading'
  const warningText = isMaster
    ? 'This will submit a cancellation request to NAIC. Once confirmed, this master bill will be marked for cancellation and cannot be reversed without NAIC support.'
    : 'This will submit a cancellation request to NAIC. Once confirmed, this house bill will be marked for cancellation and cannot be reversed without NAIC support.'

  const error = touched && reason.trim().length < MIN_CHARS
    ? `Minimum ${MIN_CHARS} characters required.`
    : ''

  function handleClose() {
    setReason('')
    setTouched(false)
    onClose()
  }

  function handleSubmit() {
    setTouched(true)
    if (reason.trim().length < MIN_CHARS) return
    onSubmit(reason.trim())
  }

  if (!bill) return null

  const infoRows = [
    { label: 'MPCI ID',         value: bill.mpciDocId     || '—' },
    isMaster
      ? { label: 'Master Bill No', value: bill.masterBillNo || '—' }
      : { label: 'House Bill No',  value: bill.houseBillNo  || '—' },
    { label: 'Vessel',          value: [bill.carrierIdentifier, bill.vesselName].filter(Boolean).join(' ') || '—' }
  ]

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialogPaper }}
      disableBackdropClick={submitting}
      disableEscapeKeyDown={submitting}
    >
      {/* Header */}
      <DialogTitle disableTypography classes={{ root: classes.titleRoot }}>
        <Box className={classes.titleRow}>
          <Typography className={classes.titleText}>
            <FontAwesomeIcon icon={['fal', 'triangle-exclamation']} />
            {title}
          </Typography>
          <IconButton size='small' className={classes.closeBtn} onClick={handleClose} disabled={submitting}>
            <FontAwesomeIcon icon={['fal', 'xmark']} />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent style={{ padding: '20px 24px 8px' }}>
        {/* Bill info box */}
        <Box className={classes.infoBox}>
          {infoRows.map((row) => (
            <Box key={row.label} className={classes.infoRow}>
              <Typography className={classes.infoLabel}>{row.label}:</Typography>
              <Typography className={classes.infoValue}>{row.value}</Typography>
            </Box>
          ))}
        </Box>

        {/* Cancellation reason */}
        <Typography variant='body2' style={{ fontWeight: 600, marginBottom: 6 }}>
          Cancellation Reason <span style={{ color: 'red' }}>*</span>
        </Typography>
        <TextField
          className={classes.textArea}
          fullWidth
          multiline
          rows={4}
          variant='outlined'
          placeholder='Provide the reason for cancelling this bill...'
          value={reason}
          onChange={(e) => {
            if (e.target.value.length <= MAX_CHARS) setReason(e.target.value)
          }}
          onBlur={() => setTouched(true)}
          error={!!error}
          helperText={error}
          disabled={submitting}
        />
        <Typography className={classes.charHint}>
          Min {MIN_CHARS} chars &middot; Max {MAX_CHARS} chars &middot; {reason.length}/{MAX_CHARS}
        </Typography>

        {/* Warning */}
        <Box className={classes.warningBox}>
          <FontAwesomeIcon icon={['fal', 'triangle-exclamation']} className={classes.warningIcon} />
          <Typography className={classes.warningText}>
            <strong>Warning:</strong> {warningText}
          </Typography>
        </Box>
      </DialogContent>

      {/* Footer */}
      <Box className={classes.footer}>
        <CngButton
          color='secondary'
          size='medium'
          disabled={submitting}
          onClick={handleClose}
          startIcon={<FontAwesomeIcon icon={['fal', 'arrow-left']} />}
        >
          Back
        </CngButton>
        <CngButton
          color='primary'
          size='medium'
          disabled={submitting}
          onClick={handleSubmit}
          startIcon={
            submitting
              ? <FontAwesomeIcon icon={['fal', 'spinner']} spin />
              : <FontAwesomeIcon icon={['fal', 'paper-plane']} />
          }
        >
          {submitting ? 'Submitting...' : 'Submit Cancellation'}
        </CngButton>
      </Box>
    </Dialog>
  )
}

export default CancelBillDialog
